import { PERMISSIONS } from '@ibe/constants';
import { StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil } from 'lucide-react';

import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { guestEndpoints } from '../api/endpoints.js';
import { GUEST_STATUS_OPTIONS, type GuestRow, guestRow } from '../api/schemas.js';

/**
 * The guests list.
 *
 * PII is not shown here: names and addresses are encrypted, and the directory
 * returns a masked display name instead. An operator who needs the plaintext
 * opens the detail page, which goes through the audited PII endpoint.
 */

const filters: readonly FilterDescriptor[] = [
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: GUEST_STATUS_OPTIONS,
  },
];

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const guestColumns: readonly ColumnDescriptor<GuestRow>[] = [
  {
    id: 'displayName',
    header: 'Guest',
    sortable: false,
    alwaysVisible: true,
    fields: ['displayName'],
    cell: (row) => <PrimaryCell label={row.displayName ?? ''} to={`/guests/${row.id}`} />,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['displayName'],
    cell: (row: GuestRow): React.JSX.Element => <GuestRowActions row={row} />,
  },
];

function GuestRowActions({ row }: { readonly row: GuestRow }): React.JSX.Element {
  const label = row.displayName ?? '';

  return (
    <RowActions
      actions={[
        { label: `View ${label}`, icon: Eye, to: `/guests/${row.id}` },
        {
          label: `Edit ${label}`,
          icon: Pencil,
          to: `/guests/${row.id}/edit`,
        },
      ]}
    />
  );
}

export function GuestListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Guests"
      description="Every guest profile for this tenant."
      path={guestEndpoints.list}
      resource="guests"
      schema={guestRow}
      columns={guestColumns}
      filters={filters}
      searchLabel="Search by email or phone"
      rowKey={(row) => row.id}
      permission={PERMISSIONS.GUEST_READ}
    />
  );
}
